import React from 'react';
import {
  Card,
  Grid,
  Typography,
  Box,
  CardContent,
  CardHeader,
  Divider,
} from '@mui/material';

import {
  Datagrid,
  TextField,
  ArrayField,
  useRecordContext,
  NumberField,
  DateField,
  SelectField,
  RecordContextProvider,
  SingleFieldList,
  ChipField,
  ReferenceArrayField,
} from 'react-admin';

import { boldDataGridStyle } from '../../../../constants/style/datagridStyles';

import ReferenceEntityField from '../../../../customFields/ReferenceEntityField';
import ReferenceLastKycRequest from '../../../../customFields/ReferenceLastKycRequest';
import { transmodeTypesChoices } from '../../../../constants/transmodeTypes';
import entityTypes from '../../../../constants/entityTypes';
import StatusField from '../../../../customFields/StatusField';
import ColoredScoreField from '../../../../customFields/ColoredScoreField';
import EntityDisplayNameField from '../../../../customFields/EntityDisplayNameField';

const boldTitlesStyle = {
  fontWeight: 'bold',
  color: 'text.primary',
};

export const TransactionDetails = ({
  compact,
  boldTitles,
}: {
  compact?: boolean;
  boldTitles?: boolean;
}) => {
  const record = useRecordContext();
  if (!record) return null;

  const { extraInformation } = record;

  const reformedEntities = {
    entities: [
      {
        requestRole: 'Initiator',
        ...extraInformation?.initiator,
      },
      {
        requestRole: 'Initiator\'s account',
        ...extraInformation?.initiator?.account,
      },
      {
        requestRole: 'Initiator\'s product',
        ...extraInformation?.initiator?.productEntity,
      },
      {
        requestRole: 'Recipient',
        ...extraInformation?.recipient,
      },
      {
        requestRole: 'Recipient\'s account',
        ...extraInformation?.recipient?.account,
      },
      {
        requestRole: 'Recipient\'s product',
        ...extraInformation?.recipient?.productEntity,
      },
      {
        requestRole: 'Processor',
        ...extraInformation?.processor,
      },
      {
        requestRole: 'Processor\'s product',
        ...extraInformation?.processor?.productEntity,
      },
      {
        requestRole: 'Sub-Processor',
        ...extraInformation?.subProcessor,
      },
      {
        requestRole: 'Sub-Processor\'s product',
        ...extraInformation?.subProcessor?.productEntity,
      },
      ...(extraInformation?.initiator?.conductors
        ? extraInformation.initiator.conductors.map(
          (conductor: { id: string; type: typeof entityTypes.INDIVIDUAL }) => ({
            requestRole: 'Conductor',
            id: conductor?.id,
            type: conductor.type,
          }),
        )
        : []
      ),
    ].filter((item) => item?.id || item?.type === entityTypes.UNKNOWN),
  };

  return (
    <RecordContextProvider value={extraInformation}>
      <Grid container spacing={compact ? 3 : 6}>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>Amount</Typography>
            <NumberField source="amount" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>Currency</Typography>
            <TextField source="currency" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>Date</Typography>
            <DateField showTime source="date" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>Status</Typography>
            <StatusField source="status" resource="transactions" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>Transaction types</Typography>
            <ReferenceArrayField source="transactionTypes" reference="transaction-types">
              <SingleFieldList linkType={false} empty={<>-</>}>
                <ChipField source="name" size={compact ? 'small' : 'medium'} />
              </SingleFieldList>
            </ReferenceArrayField>
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>Text message</Typography>
            <TextField source="textMessage" emptyText="-" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>Reference</Typography>
            <TextField source="reference" emptyText="-" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>
              Transaction channel
            </Typography>
            <SelectField choices={transmodeTypesChoices} source="transactionChannel" emptyText="-" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>
              Transaction channel provider
            </Typography>
            <TextField source="transactionChannelProvider" emptyText="-" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>
              Payer name
            </Typography>
            <TextField source="initiator.payerName" emptyText="-" />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            xl: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="smallTitle" sx={boldTitles ? boldTitlesStyle : undefined}>
              Beneficiary name
            </Typography>
            <TextField source="recipient.beneficiaryName" emptyText="-" />
          </Box>
        </Grid>
        <Grid size={12}>
          <RecordContextProvider value={reformedEntities}>
            <Card variant="outlined">
              {!compact && (
                <>
                  <CardHeader title="Involved entities" />
                  <Divider />
                </>
              )}
              <ArrayField source="entities">
                <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
                  <TextField emptyText="-" source="requestRole" label="Role" />
                  <ReferenceEntityField source="" label="Name">
                    <EntityDisplayNameField />
                  </ReferenceEntityField>
                  <EntityDisplayNameField useEntityContext={false} label="Submitted name" />
                  <TextField emptyText="-" source="type" label="Type" />
                  <ReferenceLastKycRequest source="" label="KYC approval date" />
                  <ReferenceEntityField source="" label="Risk score">
                    <ColoredScoreField source="riskScore" />
                  </ReferenceEntityField>
                  <TextField source="ip" label="IP address" emptyText="-" />
                  <TextField source="transactionCountry" label="Country" emptyText="-" />
                </Datagrid>
              </ArrayField>
            </Card>
          </RecordContextProvider>
        </Grid>
      </Grid>
    </RecordContextProvider>
  );
};

const Transaction = () => (
  <Grid size={12}>
    <Grid container spacing={6}>
      <Grid size={12}>
        <Card variant="outlined">
          <CardHeader title="Transaction details" />
          <Divider />
          <CardContent>
            <TransactionDetails />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Grid>
);

export default Transaction;
